import { compile as handlebarsCompile } from "handlebars";
import * as RestLessBookmarksLocalState from "./bookmarks-state-local";
import { BookmarkType, BookmarkTypes } from "./bookmarks-config";
import * as BookmarksClient from "./bookmarks-client";
import { CookieStore } from "@common/core/CookieStore";
import { BookmarksMetaCookieKeyName } from "./bookmarks-state-nav";
import { getEnvironment } from "@common/utils/env";
import { elementObserver } from "@common/utils/dom";

const RestlessManageBookmarks: TRestlessManageBookmarks = {
  selectedCategory: null,
  params: {},
  page: 1,
  pageCount: null,
  results: [],
  bookmarkTemplatesData: null,
  incomingBookmark: null,
  metadata: null,
  isBusy: false,
  init: async function () {
    this.setBusy(true);
    this.setParams();
    this.setBookmarkTemplatesData();
    this.setNavListeners();
    window.addEventListener("bookmarks-metadata", (e) => this.renderMetadata(e));

    this.setupInfiniteScrolling();

    if (this.params.bookmarkId && this.params.bookmarkType) {
      if (window.getAccessToken()) {
        await RestLessBookmarksLocalState.moveLocalBookmarksToAccount([], async (type, id) => {
          if (type != this.params.bookmarkType && id != this.params.bookmarkId) {
            // this check ensures we don't add the same bookmark twice
            this.handleBookmarkAdd(type, id, true);
          }
        });
        if (BookmarkTypes.includes(this.params.bookmarkType as BookmarkType)) {
          await this.handleBookmarkAdd(this.params.bookmarkType as BookmarkType, this.params.bookmarkId, false);
        }
        this.clearBookmarkParams();
      } else {
        if (BookmarkTypes.includes(this.params.bookmarkType as BookmarkType)) {
          await this.handleLoggedOutBookmark(this.params.bookmarkType as BookmarkType, this.params.bookmarkId);
        }
      }
    } else {
      if (window.getAccessToken()) {
        this.activeBookmarkTabs();
      } else {
        window.location.href = "/#unauthenticated";
      }
    }
  },
  getContainer: function () {
    return document.getElementById("saved-bookmarks");
  },
  getListingHarness: function () {
    return document.getElementById("bookmark-listings");
  },
  activeBookmarkTabs: function () {
    if (this.params.type && BookmarkTypes.includes(this.params.type as BookmarkType)) {
      this.changeCategory(this.params.type as BookmarkType);
    } else {
      this.getDefaultCategory();
    }
  },
  setParams: function () {
    let searchPairs = window.location.hash.replace("#", "").replace("?", "&");
    searchPairs = searchPairs ? searchPairs : window.location.search;
    const searchParams = new URLSearchParams(searchPairs);
    const searchParamsObj = Object.fromEntries(searchParams.entries());
    this.params = searchParamsObj;

    if (this.params.bookmarkId && this.params.bookmarkType) {
      this.params.bookmarkId = this.ensureIdIsRestLessId(this.params.bookmarkId, this.params.bookmarkType);
    }
  },
  ensureIdIsRestLessId: function (id: string, type: string): string {
    try {
      const bsIdTypeConvert = {
        job: "job",
        volunteering: "job",
        traveldeal: "traveldeal",
        course: "course",
        article: "post"
      }[type];

      const environment = getEnvironment();
      const blueshiftEnvironments = environment === "production" ? "production" : "staging";
      const blueshiftPrefix = `${blueshiftEnvironments}-${bsIdTypeConvert}-`;
      if (id.startsWith(blueshiftPrefix)) {
        return id.replace(blueshiftPrefix, "");
      }
      return id;
    } catch (e) {
      console.error("Error ensuring ID is RestLess ID", e);
      return id;
    }
  },
  setBookmarkTemplatesData: function () {
    if (typeof bookmarkTemplatesData !== "undefined") {
      this.bookmarkTemplatesData = bookmarkTemplatesData;
    }
  },
  setBusy: function (isBusy) {
    this.isBusy = isBusy;
    if (isBusy) {
      this.getContainer()?.classList.add("busy");
    } else {
      this.getContainer()?.classList.remove("busy");
    }
  },

  /**
   * Set up listeners for the category buttons
   */
  setNavListeners: function () {
    // Get all the bookmark category buttons and add an event handler to execute categorySelected()
    const bookmarkCategory = document.querySelectorAll(".bookmark-nav button");
    bookmarkCategory.forEach((button) => {
      const category = button.parentElement?.dataset.bookmarkcategory as BookmarkType | undefined;
      if (!category) return;
      button.addEventListener("click", () => this.changeCategory(category));
    });
  },
  /**
   * Set up listeners for the "remove bookmark" buttons
   */
  setRemoveListeners: function () {
    const removeButtons: NodeListOf<HTMLElement> = document.querySelectorAll(".remove-bookmark");

    removeButtons.forEach((button) => {
      button.addEventListener("click", () => this.launchDeleteBookmarkDialog(button));
    });
  },

  launchBookmarkAddedDialog: function (html) {
    if (!this.params.bookmarkType) return;
    const template = handlebarsCompile(html);
    const popup = document.createElement("div");

    popup.innerHTML = template({
      type: RestLessBookmarksLocalState.getBookmarkTypeDisplayName(this.params.bookmarkType)
    });
    popup.querySelectorAll(".btn-close-action").forEach((link) => {
      link.addEventListener("click", function () {
        popup.remove();
      });
    });
    document.body.appendChild(popup);
  },
  launchDeleteBookmarkDialog: function (button) {
    const dialogTemplate = document.getElementById("dialog-confirm-delete-hbs");
    if (!dialogTemplate || !this.selectedCategory) return;

    const template = Handlebars.compile(dialogTemplate.innerHTML);
    const popup = document.createElement("div");
    popup.innerHTML = template({
      type: RestLessBookmarksLocalState.getBookmarkTypeDisplayName(this.selectedCategory)
    });
    popup.querySelectorAll(".btn-close-action").forEach((link) => {
      link.addEventListener("click", function () {
        popup.remove();
      });
    });
    popup.querySelectorAll(".action-confirm").forEach((link) => {
      link.addEventListener("click", () => {
        this.removeBookmark(button);
        popup.remove();
      });
    });
    document.body.appendChild(popup);
  },
  setupInfiniteScrolling: function () {
    const loadMoreElement = document.getElementById("load-more");
    if (!loadMoreElement) return;
    elementObserver(loadMoreElement, { rootMargin: "0px 0px 0px 0px" }, () => {
      if (!this.isBusy) {
        if (this.pageCount && this.page < this.pageCount) {
          this.page++;
          this.getAjaxBookmarksFetchType();
        }
      }
    });
  },
  error: function (msg) {
    const errorDiv: HTMLElement | null = document.querySelector("#saved-bookmarks p.error");
    if (errorDiv) {
      errorDiv.innerText = msg;
    } else {
      const errorElement = document.createElement("p");
      errorElement.innerText = msg;
      errorElement.className = "error";
      this.getContainer()?.prepend(errorElement);
      errorElement.addEventListener("click", function () {
        this.remove();
      });
    }
  },
  clearError: () => {
    const errorDiv = document.querySelector("#saved-bookmarks p.error");
    if (errorDiv) {
      errorDiv.remove();
    }
  },
  changeCategory: function (category: BookmarkType) {
    if (BookmarkTypes.includes(category)) {
      this.page = 1;
      const bookmarkCategories: NodeListOf<HTMLElement> = document.querySelectorAll(".bookmark-category");
      bookmarkCategories.forEach((el) => {
        el.dataset.active = "false";
      });

      const bookmarkCategoryElm: HTMLElement | null = document.querySelector(`[data-bookmarkcategory="${category}"]`);
      if (!bookmarkCategoryElm) return;
      bookmarkCategoryElm.dataset.active = "true";
      this.selectedCategory = category;
      this.getAjaxBookmarksFetchType();
    }
  },
  getDefaultCategory: function () {
    const cookie = CookieStore.getCookie(BookmarksMetaCookieKeyName);
    let largestValue = 0;
    let largestCategory: BookmarkType | null = null;

    if (cookie) {
      const meta = JSON.parse(cookie);

      for (const i in meta) {
        if (meta[i] > largestValue) {
          largestValue = meta[i];
          largestCategory = i.replace("Total", "") as BookmarkType;
        }
      }
    }

    if (largestCategory) {
      this.params.type = largestCategory;
      this.changeCategory(largestCategory);
    } else {
      this.changeCategory("job");
    }
  },
  handleBookmarkAdd: async function (type: BookmarkType, id: string | number, silent: boolean) {
    const access_token = window.getAccessToken();
    if (!access_token) {
      return;
    }

    try {
      const formattedId = type !== "traveldeal" ? Number(id) : id;
      const findData: BookmarksClient.BookmarksFindDataParam = {
        types: [type],
        [`${type}Ids`]: [formattedId]
      };
      const [bookmarks, bookmarkDetails] = await Promise.all([
        BookmarksClient.find(findData, access_token),
        this.getBookmarkDetails(type, formattedId)
      ]);

      // This check ensures that the bookmark item actually exists before trying to add it.
      if (bookmarkDetails) {
        // Send event before we add the bookmark to the user's account.
        // Confirmed with Hugo that this is the correct order.
        const bookmarkEventData = this.getBookmarkAnalyticsData(bookmarkDetails);
        if (silent) {
          window.blueShiftEvent(type + "_bookmark_added", bookmarkEventData);
          window.gtmEvent("Bookmark", "Add", type);
        }

        if (bookmarks && bookmarks.message.results && bookmarks.message.results.length === 0) {
          // Bookmark doesn't exist already for the user, so add it.
          const result = await BookmarksClient.add(type, formattedId, access_token);
          if (!silent) {
            this.launchBookmarkAddedDialog(result.dialog);
          }
        }
      }

      this.activeBookmarkTabs();
    } catch (error) {
      // Something went wrong but we don't want to stop the user from using the page
      console.error(error);
      this.activeBookmarkTabs();
    }
  },
  handleLoggedOutBookmark: async function (type: BookmarkType, id: string | number) {
    const bookmarkDetails = await this.getBookmarkDetails(type, id);
    if (bookmarkDetails) {
      const eventData = this.getBookmarkAnalyticsData(bookmarkDetails);
      RestLessBookmarksLocalState.addBookmarkLocally(type, id, eventData, {
        closeSignupDialogCallback: () => {
          window.location.href = "/";
        }
      });
    } else {
      window.launchSignupLogin("signup", {
        allowConfirmationDialog: false,
        title: `We couldn't find that ${RestLessBookmarksLocalState.getBookmarkTypeDisplayName(type)}`,
        description: "Create an account or log in to view your bookmarks",
        closeCallback: () => {
          window.location.href = "/";
        }
      });
    }
  },

  clearBookmarkParams: function () {
    const url = new URL(window.location.toString());
    ["bookmarkId", "bookmarkType"].forEach((param) => url.searchParams.delete(param));
    window.history.pushState({}, "", url);
  },

  /**
   * Get the data to send to BlueShift for bookmark analytics
   */
  getBookmarkAnalyticsData: (bookmarkDetails: BookmarksClient.BookmarkDetails): BookmarkAnalyticsData => {
    switch (bookmarkDetails.bookmark_type) {
      case "job":
      case "volunteering":
        return {
          product_id: bookmarkDetails.bs_product_id,
          [`${bookmarkDetails.bookmark_type}_title`]: bookmarkDetails.title,
          [`${bookmarkDetails.bookmark_type}_employer`]: bookmarkDetails.employer_name,
          [`${bookmarkDetails.bookmark_type}_type`]: bookmarkDetails.type,
          [`${bookmarkDetails.bookmark_type}_location`]: bookmarkDetails.location,
          [`${bookmarkDetails.bookmark_type}_postcode`]: bookmarkDetails.postcode
        };
      case "course":
        return {
          product_id: bookmarkDetails.bs_product_id,
          course_title: bookmarkDetails.title,
          course_educator: bookmarkDetails.educator_name,
          course_study_type: bookmarkDetails.delivery_type
        };
      case "article":
        return {
          product_id: bookmarkDetails.bs_product_id,
          article_title: bookmarkDetails.title
        };

      case "traveldeal":
        return {
          product_id: bookmarkDetails.bs_product_id,
          traveldeal_title: bookmarkDetails.title,
          traveldeal_destination_friendly: bookmarkDetails.destinationFriendly,
          traveldeal_original_link: bookmarkDetails.originalLink,
          traveldeal_tracking_link: bookmarkDetails.link,
          traveldeal_price: bookmarkDetails.priceUnformatted
        };
    }
  },
  getAjaxBookmarksFetchType: async function () {
    const token = window.getAccessToken();
    if (!token) {
      return;
    }

    this.clearError();
    this.setBusy(true);

    const baseUrl = "/ajax/bookmarks/fetch_type/";
    const params = {
      type: String(this.selectedCategory),
      page: String(this.page),
      r: String(Math.random())
    };
    const url = `${baseUrl}?${new URLSearchParams(params).toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        // do success
        const responseJson = await response.json();
        this.handleResponse(responseJson);
      } else {
        // do error
        this.error("Sorry, there was a problem fetching your bookmarks, please try again");
      }
    } catch {
      this.error("Sorry, there was a problem fetching your bookmarks, please try again");
    }

    this.setBusy(false);
  },
  getBookmarkDetails: async function (bookmarkType: BookmarkType, bookmarkId: string | number): Promise<BookmarksClient.BookmarkDetails | false> {
    if (!BookmarkTypes.includes(bookmarkType)) {
      throw new Error("Invalid Bookmark Type");
    }
    const bookmarkCategory = document.querySelector(`[data-bookmarkcategory="${bookmarkType}"]`) as HTMLElement | null;
    if (!bookmarkCategory) {
      return false;
    }
    bookmarkCategory.dataset.active = "true";
    this.selectedCategory = bookmarkType;
    this.params.type = bookmarkType;

    const bookmarkDetails = await BookmarksClient.details(bookmarkType, bookmarkId);
    if (bookmarkDetails && bookmarkDetails.results && bookmarkDetails.results.length === 1) {
      return bookmarkDetails.results[0];
    } else {
      return false;
    }
  },
  handleResponse: function (response) {
    const { results, paging, metadata } = response;
    this.pageCount = Math.ceil(paging.totalCount / paging.pageSize);

    if (this.page === 1) {
      this.results = [];
    }
    this.results = this.results.concat(results);

    // Which category do we need to render?
    if (results.length === 0) {
      this.renderEmptyCategory();
    } else {
      this.renderCategory();
    }

    this.metadata = metadata;
    window.dispatchEvent(new CustomEvent("bookmarks-metadata", { detail: metadata }));
    this.setBusy(false);
  },
  renderCategory: function () {
    const bookmarkListingElm = document.getElementById("bookmarks-listing-" + this.selectedCategory);
    const listingsHarnesElm = this.getListingHarness();
    if (!bookmarkListingElm || !listingsHarnesElm || !this.selectedCategory) return;

    const categoryTemplate = Handlebars.compile(bookmarkListingElm?.innerHTML);
    // Append results to an array to allow for infinite scroll
    listingsHarnesElm.innerHTML = categoryTemplate({
      data: this.bookmarkTemplatesData[this.selectedCategory],
      results: this.results
    });

    this.setRemoveListeners();
  },
  renderEmptyCategory: function () {
    const emptyBookmarkHbs = document.getElementById("empty-bookmarks-hbs");
    const listingHarness = this.getListingHarness();
    if (!emptyBookmarkHbs || !this.selectedCategory || !listingHarness) return;

    const template = Handlebars.compile(emptyBookmarkHbs.innerHTML);
    const templateData = {
      data: this.bookmarkTemplatesData[this.selectedCategory]["empty"]
    };
    listingHarness.innerHTML = template(templateData);
  },
  renderMetadata: function (e) {
    const container = this.getContainer();
    const metadata = e.detail;
    if (!metadata || !container) {
      return;
    }

    BookmarkTypes.forEach(function (type) {
      const key = type + "Total";
      const totalElms = container?.querySelectorAll('.bookmark-total[data-type="' + type + '"]');
      if (totalElms) {
        totalElms.forEach((span) => {
          span.textContent = metadata[key];
        });
      }
    });
  },
  removeBookmark: async function (button) {
    // Access token will be fine by now (otherwise you wouldn't see the button)
    const access_token = window.getAccessToken();
    if (!access_token || !button.dataset.guid) {
      return;
    }

    this.setBusy(true);

    try {
      const response = await BookmarksClient.remove(button.dataset.guid, access_token);

      // Annoyingly, failures return a 200...
      if (response.status !== "error") {
        button.closest("li")?.remove();
        window.blueShiftEvent(button.dataset.type + "_bookmark_removed", {
          product_id: button.dataset.bsProduct_id!
        });
        this.deincrementMetadata();
      } else {
        this.error("Sorry, there was a problem deleting this bookmark, please try again");
        console.error("Failed deleting bookmark", response);
      }
    } catch (err) {
      this.error("Sorry, there was a problem deleting this bookmark, please try again");
      console.error("Failed deleting bookmark", err);
    } finally {
      this.setBusy(false);
    }
  },
  deincrementMetadata: function () {
    const key = this.selectedCategory + "Total";
    if (this.metadata && this.metadata[key]) {
      this.metadata[key]--;
    }
    if (this.metadata[key] === 0) {
      this.renderEmptyCategory();
    }
    window.dispatchEvent(new CustomEvent("bookmarks-metadata", { detail: this.metadata }));
  }
};

window.addEventListener("sso-session-status", (e) => {
  if (e.detail === "unauthenticated" || e.detail === "authenticated") {
    RestlessManageBookmarks.init();
  } else if (e.detail === "logged_out") {
    window.location.href = "/#logged_out";
  }
});

type TRestlessManageBookmarks = {
  selectedCategory: string | null;
  params:
    | {
        type?: BookmarkType;
        bookmarkId?: string;
        bookmarkType?: BookmarkType;
      }
    | Record<string, string>;
  page: number;
  pageCount: number | null;
  results: any[];
  bookmarkTemplatesData: any;
  incomingBookmark: any;
  metadata: any;
  isBusy: boolean;
  init: () => void;
  activeBookmarkTabs: () => void;
  setParams: () => void;
  getContainer: () => HTMLElement | null;
  getListingHarness: () => HTMLElement | null;
  setBookmarkTemplatesData: () => void;
  setBusy: (isBusy: boolean) => void;
  setNavListeners: () => void;
  setRemoveListeners: () => void;
  launchBookmarkAddedDialog: (html: string) => void;
  launchDeleteBookmarkDialog: (button: HTMLElement) => void;
  ensureIdIsRestLessId: (id: string, type: string) => string;
  setupInfiniteScrolling: () => void;
  error: (msg: string) => void;
  clearError: () => void;
  changeCategory: (category: BookmarkType) => void;
  getDefaultCategory: () => void;
  handleBookmarkAdd: (type: BookmarkType, id: string | number, silent: boolean) => Promise<void>;
  handleLoggedOutBookmark: (type: BookmarkType, id: string | number) => Promise<void>;
  clearBookmarkParams: () => void;
  getBookmarkAnalyticsData: (bookmarkDetails: BookmarksClient.BookmarkDetails) => Record<string, string | number>;
  getAjaxBookmarksFetchType: () => void;
  getBookmarkDetails: (bookmarkType: BookmarkType, bookmarkId: string | number) => Promise<BookmarksClient.BookmarkDetails | false>;
  handleResponse: (response: any) => void;
  renderCategory: () => void;
  renderEmptyCategory: () => void;
  renderMetadata: (e: CustomEvent) => void;
  removeBookmark: (button: HTMLElement) => void;
  deincrementMetadata: () => void;
};

type BookmarkAnalyticsData = {
  product_id: string;
  job_title?: string;
  job_employer?: string;
  job_type?: string;
  job_location?: string;
  job_postcode?: string;
  course_title?: string;
  course_educator?: string;
  course_study_type?: string;
  article_title?: string;
  traveldeal_title?: string;
  traveldeal_destination_friendly?: string;
  traveldeal_original_link?: string;
  traveldeal_tracking_link?: string;
  traveldeal_price?: number;
};

// bookmarkTemplatesData is available on the page JS so create a type
declare global {
  const bookmarkTemplatesData: any;
}
